import {
  DataCatalogApolloClient, GraphDataQualityWarning, VizClientDataCatalogProvider, WarningStatusChip, WarningStatusIcon, WarningStatusProps,
  defaultFeatureFlagsWithOverrides,
  GraphQLErrorCode,
  FeatureFlagsContext
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Query } from 'react-apollo';
import {
  DataQualityWarningsInWebAuthoringQuery,
  DataQualityWarningsInWebAuthoringQueryVariables,
  DataQualityWarningsInWebAuthoringQuery_databaseServersConnection_nodes,
  DataQualityWarningsInWebAuthoringQuery_databaseTablesConnection_nodes,
  DataQualityWarningsInWebAuthoringQuery_flowsConnection_nodes,
  DataQualityWarningsInWebAuthoringQuery_publishedDatasourcesConnection_nodes,
  DataQualityWarningsInWebAuthoringQuery_workbooksConnection_nodes,
} from '../../../gen-src/queries/DataQualityWarningsInWebAuthoringQuery';
import { DataQualityWarningsInWebAuthoringQueryGql } from '../../queries/DataQualityWarningsInWebAuthoringQueryGql';
import { renderQueryResult } from '../renderQueryResult';
import { KeyValueStore } from './keyValueStoreGenerator';

/**
 * C# + Saltarelle is gross, the ID arrays can be arrays with undefined entries,
 * so the props for this component should reflect that.
 */
export type DataQualityWarningsInWebAuthoringProps = {
  readonly client: DataCatalogApolloClient;
  readonly siteUrlName: string;
  readonly queryString: string;
  readonly iconOnly: boolean;
  readonly datasourceName: string;
  readonly datasourceCaption: string;
  readonly workbookIds: ReadonlyArray<string | null | undefined>;
  readonly flowIds: ReadonlyArray<string | null | undefined>;
  readonly publishedDatasourceIds: ReadonlyArray<string | null | undefined>;
  readonly databaseServerIds: ReadonlyArray<string | null | undefined>;
  readonly databaseTableIds: ReadonlyArray<string | null | undefined>;
  readonly keyValueStore: KeyValueStore;
};

/**
 * Since the ttl is just used to mask flickering we decided to not make it configurable and just keep it relatively small
 * */
const cacheTimeToLiveInMillis = 60000;

type WarningStatusPropsWithExpiry = {
  readonly warning: WarningStatusProps;
  readonly expiry: number;
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function DataQualityWarningsInWebAuthoring(props: DataQualityWarningsInWebAuthoringProps): JSX.Element {
  const { datasourceName, datasourceCaption, keyValueStore: store } = props;

  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const setPlaceholderWarning = React.useCallback((warning: WarningStatusProps | null) => {
    const key = `${datasourceName}-${datasourceCaption}`;
    if (warning === null) {
      store.removeItem(key);
    } else {
      const cache: WarningStatusPropsWithExpiry = {
        warning: { ...warning },
        expiry: new Date().getTime() + cacheTimeToLiveInMillis
      }
      store.setItem(key, JSON.stringify(cache));
    }
  }, [datasourceName, datasourceCaption, store]);

  const { client, siteUrlName, queryString, iconOnly } = props;

  const [renderers, siteUrls, featureFlags] = React.useMemo(() => {
    const siteUrls = new SiteUrls({ siteUrlName, queryString });
    const featureFlags = defaultFeatureFlagsWithOverrides({
      dqcertificationVisualDesignEnabled: true,
      markupEditingEnabled: true,
    });

    return [
      {
        renderData: renderWarnings(siteUrls, iconOnly, setPlaceholderWarning),
        renderLoading: renderCachedWarningsIfPresent(siteUrls, iconOnly, datasourceName, datasourceCaption, store),
        renderDataWithError: renderWarnings(siteUrls, iconOnly, setPlaceholderWarning),
        renderError: (_: GraphQLErrorCode | null) => {
          setPlaceholderWarning(null);
          return null;
        },
        renderNoData: () => {
          setPlaceholderWarning(null);
          return null;
        }
      },
      siteUrls,
      featureFlags
    ];
  }, [siteUrlName, queryString, iconOnly, datasourceName, datasourceCaption, setPlaceholderWarning, store]);

  const { workbookIds, flowIds, publishedDatasourceIds, databaseServerIds, databaseTableIds } = props;

  const warningStatusPropsWithExpiry = store.tryGetAndParse<WarningStatusPropsWithExpiry>(`${datasourceName}-${datasourceCaption}`);

  const now = new Date().getTime()
  if (warningStatusPropsWithExpiry !== null && warningStatusPropsWithExpiry.expiry > now) {

    return (
      iconOnly
        ? <WarningStatusIcon {...warningStatusPropsWithExpiry.warning} urls={siteUrls} />
        : <WarningStatusChip {...warningStatusPropsWithExpiry.warning} urls={siteUrls} />
    );
  }

  const variables: DataQualityWarningsInWebAuthoringQueryVariables = {
    workbookIds: [...workbookIds.filter(notEmpty)],
    flowIds: [...flowIds.filter(notEmpty)],
    publishedDatasourceIds: [...publishedDatasourceIds.filter(notEmpty)],
    databaseServerIds: [...databaseServerIds.filter(notEmpty)],
    databaseTableIds: [...databaseTableIds.filter(notEmpty)],
    publishedConnectionsLineageEnabled

  };

  return (
    <VizClientDataCatalogProvider
      client={client}
      siteUrls={siteUrls}
      featureFlags={featureFlags}
    >
      <Query<DataQualityWarningsInWebAuthoringQuery, DataQualityWarningsInWebAuthoringQueryVariables>
        query={DataQualityWarningsInWebAuthoringQueryGql}
        variables={variables}
      >
        {
          result => renderQueryResult(result, renderers)
        }
      </Query>
    </VizClientDataCatalogProvider>
  );
}

function renderCachedWarningsIfPresent(urls: SiteUrls, iconOnly: boolean, datasourceName: string, datasourceCaption: string, store: KeyValueStore): () => JSX.Element {
  return (): JSX.Element => {
    const warningStatusProps = store.tryGetAndParse<WarningStatusPropsWithExpiry>(`${datasourceName}-${datasourceCaption}`);
    if (!warningStatusProps) {
      return <></>;
    }

    return (
      iconOnly
        ? <WarningStatusIcon {...warningStatusProps.warning} urls={urls} />
        : <WarningStatusChip {...warningStatusProps.warning} urls={urls} />
    );
  };
}

function renderWarnings(urls: SiteUrls, iconOnly: boolean, setPlaceholderWarning: (warning: WarningStatusProps | null) => void): (data: DataQualityWarningsInWebAuthoringQuery) => JSX.Element | null {
  return (data: DataQualityWarningsInWebAuthoringQuery): JSX.Element | null => {
    if (!data) {
      return null;
    }

    const wb = data.workbooksConnection?.nodes ?? [];
    const fl = data.flowsConnection?.nodes ?? [];
    const ds = data.publishedDatasourcesConnection?.nodes ?? [];
    const db = data.databaseServersConnection?.nodes ?? [];
    const tb = data.databaseTablesConnection?.nodes ?? [];

    if (wb.length === 0 && fl.length === 0 && ds.length === 0 && db.length === 0 && tb.length === 0) {
      return null;
    }

    const upstreamWarnings = uniqueWarnings(wb, fl, ds, db, tb);

    if (upstreamWarnings.length === 0) {
      setPlaceholderWarning(null);
      return null;
    }

    const warningStatusProps: WarningStatusProps = {
      subjectArea: null,
      kind: 'graph',
      anchoredAsset: null,
      anchoredWarnings: [],
      iconOnly,
      urls,
      upstreamWarnings,
    }

    setPlaceholderWarning(warningStatusProps);

    return (
      iconOnly
        ? <WarningStatusIcon {...warningStatusProps} />
        : <WarningStatusChip {...warningStatusProps} />
    );
  }
}

type AuthoringQueryItem = {
  dataQualityWarnings: GraphDataQualityWarning[];
  upstreamDataQualityWarningsConnection: null | {
    nodes: GraphDataQualityWarning[];
  }
};

function uniqueWarnings(
  wb: ReadonlyArray<DataQualityWarningsInWebAuthoringQuery_workbooksConnection_nodes>,
  fl: ReadonlyArray<DataQualityWarningsInWebAuthoringQuery_flowsConnection_nodes>,
  ds: ReadonlyArray<DataQualityWarningsInWebAuthoringQuery_publishedDatasourcesConnection_nodes>,
  db: ReadonlyArray<DataQualityWarningsInWebAuthoringQuery_databaseServersConnection_nodes>,
  tb: ReadonlyArray<DataQualityWarningsInWebAuthoringQuery_databaseTablesConnection_nodes>
): ReadonlyArray<GraphDataQualityWarning> {
  const seen: Set<string> = new Set();
  const result = [...flattenWorkbooks(wb), ...flattenWarnings(fl), ...flattenWarnings(ds), ...flattenWarnings(db), ...flattenWarnings(tb)];

  return result.filter(it => {
    if (seen.has(it.vizportalId)) {
      return false;
    } else {
      seen.add(it.vizportalId);
      return true;
    }
  }).sort(it => it.isSevere ? -1 : 1);
}

function flattenWorkbooks(items: ReadonlyArray<Pick<AuthoringQueryItem, 'upstreamDataQualityWarningsConnection'>>): ReadonlyArray<GraphDataQualityWarning> {
  if (items.length === 0) {
    return [];
  }

  return flattenWarnings(items.map(it => { return { ...it, dataQualityWarnings: [] } }))
}

function flattenWarnings(items: ReadonlyArray<AuthoringQueryItem>): ReadonlyArray<GraphDataQualityWarning> {
  if (items.length === 0) {
    return [];
  }

  const nestedWarnings = items
    .map(it =>
      [
        ...(it.dataQualityWarnings ? it.dataQualityWarnings : []),
        ...(it.upstreamDataQualityWarningsConnection?.nodes ?? [])
      ]
    );

  return nestedWarnings.reduce((a, b) => a.concat(b), []);
}
